import React from "react";
import { useNavigate, NavLink } from "react-router-dom";
import "./NavBar.scss";
import "./NavBarMinuteTracker.scss";
import { FiGrid, FiCamera, FiUser, FiSettings, FiMic } from "react-icons/fi";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButtonMui from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Cookies from "js-cookie";

import AudioRecorder from "../AudioRecorder/Audio3second";

const CONSENT_BUNDLE_CREATION_PATH =
  "/consent_bundle/submit_treatment_plan_for_consent_bundle_creation";
const CONSENT_BUNDLE_CREATED_EVENT = "medicube:consent-bundle-created";
// Change this to true when the minute tracker and microphone controls are needed again.
const SHOW_MINUTE_TRACKER_AND_MIC = true;

const TRANSCRIPTION_MINUTES_PATH = "/audio-recording/get_transcription_minutes";
const TRANSCRIPTION_MINUTES_API_BASE = (
  process.env.REACT_APP_BASE_URL_V2 || "https://dev.api.medicube.health/api/v2"
).replace(/\/+$/, "");

type TranscriptionUsage = { used: number; limit?: number; remaining?: number };

// Prefer confirmed API fields while retaining legacy aliases.
const parseTranscriptionUsage = (response: unknown): TranscriptionUsage => {
  const envelope = response as Record<string, unknown> | null;
  const roots = [envelope?.data, envelope?.result, response];
  for (const root of roots) {
    if (!root || typeof root !== "object" || Array.isArray(root)) continue;
    const readMinutes = (keys: string[]) => {
      for (const key of keys) {
        const raw = (root as Record<string, unknown>)[key];
        if (typeof raw !== "number" && typeof raw !== "string") continue;
        if (typeof raw === "string" && !raw.trim()) continue;
        const value = Number(raw);
        if (Number.isFinite(value) && value >= 0) return value;
      }
      return undefined;
    };
    const used = readMinutes([
      "total_used_minutes",
      "used_minutes", "minutes_used", "transcription_minutes_used",
      "total_transcription_minutes", "transcription_minutes",
      "audio_minutes_used", "usageMinutesUsed",
    ]);
    const limit = readMinutes([
      "allocated_minutes",
      "limit_minutes", "minutes_limit", "transcription_minutes_limit",
      "total_minutes", "monthly_limit", "audio_minutes_limit", "usageMinutesLimit",
    ]);
    const remaining = readMinutes(["remaining_minutes", "minutes_remaining"]);
    if (used !== undefined) {
      return { used, limit: limit ?? (remaining !== undefined ? used + remaining : undefined), remaining };
    }
    if (limit !== undefined && remaining !== undefined && remaining <= limit) {
      return { used: limit - remaining, limit, remaining };
    }
  }
  throw new Error("Unrecognized transcription usage response.");
};

const getConsentBundleCreationUrl = () => {
  const v2Base = (process.env.REACT_APP_BASE_URL_V2 || "")
    .replace(/\/+$/, "")
    .replace(/\/api\/v2$/i, "/api/v1");

  const v1Base = v2Base || "https://dev.api.medicube.health/api/v1";
  return `${v1Base}${CONSENT_BUNDLE_CREATION_PATH}`;
};

const getApiErrorMessage = (error: any) =>
  error?.response?.data?.detail ||
  error?.response?.data?.error ||
  error?.message ||
  "";

const getConsentBundleId = (value: any) => {
  const consentBundleId =
    value?.consent_bundle_id ??
    value?.consentBundleId ??
    value?.consent_bundle?.id ??
    value?.consentBundle?.id ??
    value?.data?.consent_bundle_id ??
    value?.data?.consentBundleId ??
    value?.data?.consent_bundle?.id ??
    value?.data?.consentBundle?.id;

  return consentBundleId == null || String(consentBundleId).trim() === ""
    ? ""
    : String(consentBundleId);
};

const NavBar: React.FC = () => {
  const navigate = useNavigate();

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );

  const [isAudioModalOpen, setIsAudioModalOpen] = React.useState(false);
  const [treatmentId, setTreatmentId] = React.useState("");
  const shouldOpenConsentBundleAfterSaveRef = React.useRef(false);
  const knownConsentBundleIdRef = React.useRef("");

  const handleCreateConsent = () => {
    navigate("/new-consentBundle");
  };

  const handleOpenAudioModal = () => {
    shouldOpenConsentBundleAfterSaveRef.current = false;
    knownConsentBundleIdRef.current = "";
    setTreatmentId("");
    setIsAudioModalOpen(true);
  };

  const handleCloseAudioModal = React.useCallback(() => {
    shouldOpenConsentBundleAfterSaveRef.current = false;
    knownConsentBundleIdRef.current = "";
    setIsAudioModalOpen(false);
  }, []);

  const hostname = window.location.hostname;
  const isDevEnvironment =
    hostname === "localhost" || hostname.includes("dev.medicube");

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const logOut = () => {
    Cookies.set("token", "");
    delete axios.defaults.headers.common.Authorization;
    handleCloseUserMenu();
    window.location.replace("/");
  };

  const userQuery = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const { data } = await axios.get("/user", {
        headers: { Accept: "application/json" },
      });

      const u = Array.isArray(data)
        ? typeof data[1] === "object" && data[1] !== null
          ? data[1]
          : data[0]
        : data;

      return u as any;
    },
  });

  const user = userQuery.data;

  const transcriptionUsageQuery = useQuery({
    queryKey: [
      "transcription-minutes",
      TRANSCRIPTION_MINUTES_API_BASE,
      user?.id ?? user?.user_id ?? user?.email,
      user?.medicube_practice_id,
    ],
    queryFn: async ({ signal }) => {
      const authorization =
        axios.defaults.headers.common.Authorization ??
        axios.defaults.headers.common.authorization;
      const cookieToken = Cookies.get("token");
      const token = authorization
        ? String(authorization)
        : cookieToken
          ? cookieToken.startsWith("Bearer ") ? cookieToken : `Bearer ${cookieToken}`
          : "";
      const { data } = await axios.get<unknown>(
        `${TRANSCRIPTION_MINUTES_API_BASE}${TRANSCRIPTION_MINUTES_PATH}`,
        {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: token } : {}),
          },
          withCredentials: false,
          signal,
          timeout: 15_000,
        },
      );
      return parseTranscriptionUsage(data);
    },
    enabled: SHOW_MINUTE_TRACKER_AND_MIC && !!user,
    staleTime: 0,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 1,
  });

  const refreshTranscriptionMinutes = transcriptionUsageQuery.refetch;
  React.useEffect(() => {
    if (!SHOW_MINUTE_TRACKER_AND_MIC || !user) return;
    void refreshTranscriptionMinutes();
  }, [isAudioModalOpen, user, refreshTranscriptionMinutes]);

  const rawUsedMinutes =
    transcriptionUsageQuery.data?.used ??
    user?.audio_minutes_used ??
    user?.transcription_minutes_used ??
    user?.minutes_used ??
    0;

  const rawMinuteLimit =
    transcriptionUsageQuery.data?.limit ??
    user?.audio_minutes_limit ??
    user?.transcription_minutes_limit ??
    user?.minutes_limit ??
    300;

  const usedMinutesNumber = Number(rawUsedMinutes);
  const minuteLimitNumber = Number(rawMinuteLimit);

  const usedMinutes = Number.isFinite(usedMinutesNumber)
    ? Math.max(0, usedMinutesNumber)
    : 0;

  const minuteLimit =
    Number.isFinite(minuteLimitNumber) && minuteLimitNumber >= 0
      ? minuteLimitNumber
      : 300;

  const remainingMinutes = transcriptionUsageQuery.data?.remaining ??
    Math.max(0, minuteLimit - usedMinutes);
  const formatMinutes = (value: number) =>
    value.toLocaleString("en-US", { maximumFractionDigits: 2 });

  const minuteUsagePercent = Math.min(
    100,
    Math.max(0, minuteLimit > 0 ? (usedMinutes / minuteLimit) * 100 : 0),
  );

  const usageTrackerStatus = transcriptionUsageQuery.isError
    ? "Usage could not be refreshed. Displaying the last available values."
    : !transcriptionUsageQuery.data
      ? "Loading transcription usage. Displaying fallback values."
      : "Transcription minutes used";

  const practiceIdRaw = user?.medicube_practice_id;
  const practiceId = practiceIdRaw != null ? Number(practiceIdRaw) : undefined;

  const getPracticeData = useQuery({
    queryKey: ["practice", practiceId],
    queryFn: async () => {
      const { data } = await axios.get(`/practice/${practiceId}`);
      return data;
    },
    enabled: !!practiceId,
  });

  const practiceName = getPracticeData.data?.name;

  const tooltipTitle =
    practiceName ||
    (getPracticeData.isLoading ? "Loading practice..." : "My Account");

  const avatarSrc = user?.image_url || "/user-avatar.png";
  const fallbackLetter = (user?.first_name?.trim()?.[0] || "U").toUpperCase();

  const normalizedTreatmentId = treatmentId.trim();

  const findConsentBundleIdForTreatment = React.useCallback(
    async (planId: string) => {
      const base = (process.env.REACT_APP_BASE_URL_V2 || "").replace(
        /\/+$/,
        "",
      );

      if (!base) return "";

      const { data } = await axios.get(
        `${base}/audio-recording/get_all_audio_recordings`,
      );
      const records = Array.isArray(data)
        ? data
        : (data?.audio_recordings ?? data?.recordings ?? data?.data ?? []);

      if (!Array.isArray(records)) return "";

      const matchingRecord = records.find(
        (record: any) =>
          String(record?.treatment_plan_id ?? record?.treatmentPlanId ?? "") ===
          planId,
      );

      return getConsentBundleId(matchingRecord);
    },
    [],
  );

  const handlePatientNoteSavedFromHeader = React.useCallback(
    (payload?: { response?: any }) => {
      void refreshTranscriptionMinutes();
      shouldOpenConsentBundleAfterSaveRef.current = true;

      const responseConsentBundleId = getConsentBundleId(payload?.response);
      if (responseConsentBundleId) {
        knownConsentBundleIdRef.current = responseConsentBundleId;
      }
    },
    [refreshTranscriptionMinutes],
  );

  const handleAudioRecorderRequestClose = React.useCallback(async () => {
    const shouldOpenConsentBundle = shouldOpenConsentBundleAfterSaveRef.current;
    const planId = normalizedTreatmentId;
    let consentBundleId = knownConsentBundleIdRef.current;

    handleCloseAudioModal();

    if (!shouldOpenConsentBundle) {
      navigate("/dashboard?view=transcription");
      return;
    }

    if (!consentBundleId) {
      try {
        consentBundleId = await findConsentBundleIdForTreatment(planId);
      } catch (error) {
        console.error(
          "Failed to resolve the consent bundle after saving the patient note:",
          error,
        );
      }
    }

    const params = new URLSearchParams({
      step: "1",
      transcription: "true",
      from: "header",
      patientNoteStatus: "edited",
      focus: "ai-patient-letter-generator",
    });

    if (consentBundleId) {
      params.set("consentBundleId", consentBundleId);

      try {
        const token =
          axios.defaults.headers.common.Authorization ??
          axios.defaults.headers.common.authorization;
        const { data } = await axios.get(
          `/consent_bundle/${encodeURIComponent(consentBundleId)}`,
          {
            headers: {
              Accept: "application/json",
              ...(token ? { Authorization: String(token) } : {}),
            },
          },
        );
        const consentBundleData = data?.consent_bundle ?? data;

        sessionStorage.setItem(
          "activeTreatmentData",
          JSON.stringify(consentBundleData),
        );
      } catch (error) {
        console.error(
          "Failed to preload the consent bundle after saving the patient note:",
          error,
        );
      }
    }

    navigate(`/new-consentBundle?${params.toString()}`);
  }, [
    findConsentBundleIdForTreatment,
    handleCloseAudioModal,
    navigate,
    normalizedTreatmentId,
  ]);

  const handleCreateConsentBundleIfMissing = React.useCallback(
    async (planId: string) => {
      try {
        const { data } = await axios.post(getConsentBundleCreationUrl(), {
          id: planId,
          mode: "consent_bundle",
        });

        const createdConsentBundleId = getConsentBundleId(data);
        if (createdConsentBundleId) {
          knownConsentBundleIdRef.current = createdConsentBundleId;
        }

        window.dispatchEvent(
          new CustomEvent(CONSENT_BUNDLE_CREATED_EVENT, {
            detail: { treatmentPlanId: planId },
          }),
        );

        return createdConsentBundleId || data;
      } catch (error: any) {
        throw new Error(
          getApiErrorMessage(error) ||
            "Failed to create the consent bundle. Please try again.",
        );
      }
    },
    [],
  );

  const handleHeaderAudioContextChange = React.useCallback(
    (context: {
      treatmentPlanId: string;
      consentBundleId: string;
      isVerified: boolean;
    }) => {
      setTreatmentId(context.treatmentPlanId);

      if (context.consentBundleId) {
        knownConsentBundleIdRef.current = context.consentBundleId;
      } else if (!context.isVerified) {
        knownConsentBundleIdRef.current = "";
      }
    },
    [],
  );

  return (
    <>
      {isDevEnvironment && (
        <div className="mc-env-banner">Development Sandbox Environment</div>
      )}

      <header className="mc-nav-shell">
        <div className="mc-nav">
          <div className="mc-nav__top">
            <div className="mc-nav__brand">
              <img
                className="mc-nav__logo"
                src="/logo.png"
                alt="Medicube"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>

            <nav className="mc-nav__links" aria-label="Main">
              <NavLink
                to="/welcome"
                className={({ isActive }) =>
                  `mc-nav__link ${isActive ? "is-active" : ""}`
                }
              >
                <FavoriteBorderIcon className="mc-nav__icon" />
                <span className="mc-nav__label">Welcome</span>
              </NavLink>

              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  `mc-nav__link ${isActive ? "is-active" : ""}`
                }
              >
                <FiGrid className="mc-nav__icon" />
                <span className="mc-nav__label">Dashboard</span>
              </NavLink>

              <NavLink
                to="/video-gallery"
                className={({ isActive }) =>
                  `mc-nav__link ${isActive ? "is-active" : ""}`
                }
              >
                <FiCamera className="mc-nav__icon" />
                <span className="mc-nav__label">Video Gallery</span>
              </NavLink>

              <NavLink
                to="/templatesPagev2"
                className={({ isActive }) =>
                  `mc-nav__link ${isActive ? "is-active" : ""}`
                }
              >
                <FiUser className="mc-nav__icon" />
                <span className="mc-nav__label">My Consent Templates</span>
              </NavLink>

              <NavLink
                to="/my-plan"
                className={({ isActive }) =>
                  `mc-nav__link ${isActive ? "is-active" : ""}`
                }
              >
                <FiSettings className="mc-nav__icon" />
                <span className="mc-nav__label">My Plan</span>
              </NavLink>
            </nav>

            <div className="mc-nav__actions">
              {/* Transcription balance and recording controls. */}
              {SHOW_MINUTE_TRACKER_AND_MIC && (
                <div
                  className="mc-minute-tracker"
                  aria-busy={transcriptionUsageQuery.isFetching}
                  aria-label={`${formatMinutes(usedMinutes)} of ${formatMinutes(minuteLimit)} audio minutes used; ${formatMinutes(remainingMinutes)} minutes remaining`}
                >
                  <Tooltip
                    title={`${formatMinutes(usedMinutes)} / ${formatMinutes(minuteLimit)} min used. ${formatMinutes(remainingMinutes)} min remaining. ${usageTrackerStatus}`}
                    arrow
                  >
                    <div className="mc-minute-tracker__usage" tabIndex={0}
                      aria-label={`${formatMinutes(remainingMinutes)} minutes remaining; ${formatMinutes(usedMinutes)} of ${formatMinutes(minuteLimit)} minutes used`}>
                      <div className="mc-minute-tracker__text">
                        <strong>{formatMinutes(remainingMinutes)}</strong>
                        <span>min left</span>
                      </div>
                      <div className="mc-minute-tracker__progress"
                        role="progressbar" aria-label="Transcription minutes used"
                        aria-valuemin={0} aria-valuemax={minuteLimit}
                        aria-valuenow={Math.min(usedMinutes, minuteLimit)}>
                        <span className="mc-minute-tracker__progressFill"
                          style={{ width: `${minuteUsagePercent}%` }} />
                      </div>
                    </div>
                  </Tooltip>

                  <Tooltip title="Start audio recording">
                    <button
                      className="mc-minute-tracker__mic"
                      onClick={handleOpenAudioModal}
                      type="button"
                      aria-label="Start audio recording"
                    >
                      <FiMic />
                    </button>
                  </Tooltip>
                </div>
              )}

              <button
                className="mc-btn mc-btn--primary"
                onClick={handleCreateConsent}
                type="button"
              >
                Create consent form
              </button>

              <Tooltip title={tooltipTitle}>
                <IconButton
                  className="mc-nav__avatarBtn"
                  onClick={handleOpenUserMenu}
                  aria-controls={anchorElUser ? "user-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={anchorElUser ? "true" : undefined}
                >
                  <Avatar className="mc-nav__avatar" alt="User" src={avatarSrc}>
                    {fallbackLetter}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>

        <Menu
          id="user-menu"
          anchorEl={anchorElUser}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              handleCloseUserMenu();
              navigate("/mobile_uploader");
            }}
          >
            <Typography textAlign="center">Image uploader</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleCloseUserMenu();
              navigate("/tutorials");
            }}
          >
            <Typography textAlign="center">My Tutorials</Typography>
          </MenuItem>

          <MenuItem onClick={logOut}>
            <Typography textAlign="center">Logout</Typography>
          </MenuItem>
        </Menu>
      </header>

      <Dialog
        open={isAudioModalOpen}
        onClose={handleCloseAudioModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            maxHeight: "92vh",
          },
        }}
      >
        <div className="mc-audio-modal__header">
          <div className="mc-audio-modal__headerContent">
            <div>
              <h2>Audio recording</h2>
            </div>
          </div>

          <IconButtonMui
            onClick={handleCloseAudioModal}
            aria-label="Close audio recorder"
            size="small"
          >
            <CloseIcon />
          </IconButtonMui>
        </div>

        <DialogContent className="mc-audio-modal__content">
          <AudioRecorder
            consentBundleId=""
            treatmentPlanId=""
            isFromHeader
            apiBaseUrl={process.env.REACT_APP_BASE_URL_V2}
            usageMinutesUsed={usedMinutes}
            usageMinutesLimit={minuteLimit}
            onPatientNoteReady={() => { void refreshTranscriptionMinutes(); }}
            onRequestClose={handleAudioRecorderRequestClose}
            onPatientNoteSaved={handlePatientNoteSavedFromHeader}
            onCreateConsentBundleIfMissing={handleCreateConsentBundleIfMissing}
            onHeaderContextChange={handleHeaderAudioContextChange}
            uploadEnabled
            matchConsentBundleId={false}
            uploadDisabledMessage="Enter and verify the Treatment ID before uploading audio."
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NavBar;
